import { useQuery, useQueryClient } from "@tanstack/react-query";
import { providerListingsQueryContract } from "@wags/catalog/listings/provider-listings-page.contract.ts";
import { deriveProviderListingsQueryFlags } from "./provider-listings-query-flags";
import { useRef, useState } from "react";
import {
    PROVIDER_LISTINGS_PAGE_LIMIT,
    providerListingReferencesQueryKeyPrefix,
    useInfiniteProviderListingReferences,
} from "@/features/providers";
import { usePrefetchListings } from "@/features/listings";
import {
    type CollectionWagsListing,
    useWagsListingsLiveQuery,
} from "@/features/listings";

type ListingsQuery = ReturnType<typeof useInfiniteProviderListingReferences>;

function resolveCurrentPageCards<TCard>({
    loadedPages,
    currentPageIndex,
    effectiveQuery,
    initialCards,
    fallbackImageUrl,
    mapListingToCard,
    listingById,
}: {
    loadedPages: NonNullable<ListingsQuery["data"]>["pages"];
    currentPageIndex: number;
    effectiveQuery?: string;
    initialCards: TCard[];
    fallbackImageUrl?: string;
    mapListingToCard: (
        listing: CollectionWagsListing,
        index: number,
        fallbackImageUrl: string | undefined,
    ) => TCard;
    listingById: Map<string, CollectionWagsListing>;
}) {
    const currentReferences = loadedPages.find(
        (page) => page.page === currentPageIndex,
    )?.listingReferences;
    const currentListings = currentReferences?.flatMap(({ listingId }) => {
        const listing = listingById.get(listingId);
        return listing ? [listing] : [];
    });
    const pageOffset = currentPageIndex * PROVIDER_LISTINGS_PAGE_LIMIT;
    const currentPageCards = currentListings
        ? currentListings.map((listing, index) =>
              mapListingToCard(listing, pageOffset + index, fallbackImageUrl),
          )
        : currentPageIndex === 0 && !effectiveQuery
          ? initialCards
          : [];
    return { currentPageCards, pageOffset };
}

function derivePaginationState<TCard>({
    providerListingsQuery,
    currentPageIndex,
    effectiveQuery,
    totalCount,
    initialCards,
    fallbackImageUrl,
    mapListingToCard,
    listingById,
    listingHydrationIsError,
    listingHydrationIsPending,
}: {
    providerListingsQuery: ListingsQuery;
    currentPageIndex: number;
    effectiveQuery?: string;
    totalCount: number;
    initialCards: TCard[];
    fallbackImageUrl?: string;
    mapListingToCard: (
        listing: CollectionWagsListing,
        index: number,
        fallbackImageUrl: string | undefined,
    ) => TCard;
    listingById: Map<string, CollectionWagsListing>;
    listingHydrationIsError: boolean;
    listingHydrationIsPending: boolean;
}) {
    const isPlaceholderData = providerListingsQuery.isPlaceholderData === true;
    const loadedPages = providerListingsQuery.data?.pages ?? [];
    const windowLacksFirstPage =
        !isPlaceholderData &&
        loadedPages.length > 0 &&
        !loadedPages.some((page) => page.page === 0);
    const isResettingQueryWindow =
        currentPageIndex === 0 && windowLacksFirstPage;
    const resolvedTotalCount =
        (isPlaceholderData ? undefined : loadedPages[0]?.total) ??
        (effectiveQuery ? 0 : totalCount);
    const { currentPageCards, pageOffset } = resolveCurrentPageCards({
        loadedPages,
        currentPageIndex,
        effectiveQuery,
        initialCards,
        fallbackImageUrl,
        mapListingToCard,
        listingById,
    });
    const hasCachedNextPage =
        !isPlaceholderData &&
        Boolean(
            loadedPages.find((page) => page.page === currentPageIndex + 1)
                ?.listingReferences.length,
        );
    const hasNextPage =
        hasCachedNextPage ||
        (!isPlaceholderData && providerListingsQuery.hasNextPage) ||
        (!providerListingsQuery.data &&
            !effectiveQuery &&
            totalCount > PROVIDER_LISTINGS_PAGE_LIMIT);
    const flags = deriveProviderListingsQueryFlags({
        query: providerListingsQuery,
        isPlaceholderData,
        isResettingQueryWindow,
        hasQuery: Boolean(effectiveQuery),
        currentPageCardsLength: currentPageCards.length,
        listingHydrationIsError,
        listingHydrationIsPending,
    });
    return {
        ...flags,
        loadedPages,
        isResettingQueryWindow,
        resolvedTotalCount,
        pageOffset,
        currentPageCards,
        hasCachedNextPage,
        hasNextPage,
    };
}

export function useProviderListingsPagination<TCard>({
    initialCards,
    providerId,
    totalCount,
    query,
    fallbackImageUrl,
    mapListingToCard,
}: {
    initialCards: TCard[];
    providerId: string;
    totalCount: number;
    query?: string;
    fallbackImageUrl?: string;
    mapListingToCard: (
        listing: CollectionWagsListing,
        index: number,
        fallbackImageUrl: string | undefined,
    ) => TCard;
}) {
    const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
    const [isDesktopDialogOpen, setIsDesktopDialogOpen] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const navigationInFlightRef = useRef(false);
    const queryClient = useQueryClient();

    const effectiveQuery =
        providerListingsQueryContract.parse(query ?? "") || undefined;

    const [pagedQuery, setPagedQuery] = useState(effectiveQuery);
    if (pagedQuery !== effectiveQuery) {
        setPagedQuery(effectiveQuery);
        setCurrentPageIndex(0);
    }

    const effectiveQueryRef = useRef(effectiveQuery);
    effectiveQueryRef.current = effectiveQuery;
    const isViewAllOpen = isMobileSheetOpen || isDesktopDialogOpen;

    const [wasViewAllOpen, setWasViewAllOpen] = useState(isViewAllOpen);
    if (wasViewAllOpen !== isViewAllOpen) {
        setWasViewAllOpen(isViewAllOpen);
        if (isViewAllOpen) {
            setCurrentPageIndex(0);
        }
    }
    const providerListingsQuery = useInfiniteProviderListingReferences(
        providerId,
        isViewAllOpen,
        effectiveQuery,
    );
    const loadedListingIds = (providerListingsQuery.data?.pages ?? []).flatMap(
        (page) => page.listingReferences.map(({ listingId }) => listingId),
    );
    const listingHydration = usePrefetchListings(loadedListingIds);
    const { data: loadedListings } = useWagsListingsLiveQuery(loadedListingIds);
    const listingById = new Map(
        (loadedListings ?? []).map((listing) => [listing.id, listing]),
    );
    const currentListingIds =
        providerListingsQuery.data?.pages
            .find((page) => page.page === currentPageIndex)
            ?.listingReferences.map(({ listingId }) => listingId) ?? [];
    const currentPageHasMissingListings = currentListingIds.some(
        (listingId) => !listingById.has(listingId),
    );
    const listingHydrationIsError =
        listingHydration.isError ||
        (listingHydration.isSuccess && currentPageHasMissingListings);
    const listingHydrationIsPending =
        currentPageHasMissingListings && !listingHydrationIsError;

    const pagination = derivePaginationState({
        providerListingsQuery,
        currentPageIndex,
        effectiveQuery,
        totalCount,
        initialCards,
        fallbackImageUrl,
        mapListingToCard,
        listingById,
        listingHydrationIsError,
        listingHydrationIsPending,
    });
    const {
        hasPageError,
        loadedPages,
        isResettingQueryWindow,
        resolvedTotalCount,
        pageOffset,
        currentPageCards,
        hasCachedNextPage,
        hasNextPage,
        isPageLoading,
        isSearching,
        panelState,
    } = pagination;
    useQuery({
        queryKey: ["provider-listings-window-reset", providerId],
        queryFn: async () => {
            await queryClient.resetQueries({
                queryKey: providerListingReferencesQueryKeyPrefix(providerId),
            });
            return true;
        },
        enabled: isViewAllOpen && isResettingQueryWindow,
        staleTime: 0,
    });

    const goToPreviousPage = async () => {
        if (navigationInFlightRef.current || currentPageIndex === 0) {
            return;
        }

        navigationInFlightRef.current = true;
        const previousPageIndex = currentPageIndex - 1;
        const queryAtNavigationStart = effectiveQuery;
        try {
            const hasCachedPreviousPage = loadedPages.some(
                (page) =>
                    page.page === previousPageIndex &&
                    page.listingReferences.length > 0,
            );
            if (hasCachedPreviousPage) {
                setCurrentPageIndex(previousPageIndex);
                return;
            }

            const result = await providerListingsQuery.fetchPreviousPage();
            const previousPage = result.data?.pages.find(
                (page) => page.page === previousPageIndex,
            );

            if (effectiveQueryRef.current !== queryAtNavigationStart) {
                return;
            }

            if (previousPage?.listingReferences.length) {
                setCurrentPageIndex(previousPageIndex);
            }
        } finally {
            queueMicrotask(() => {
                navigationInFlightRef.current = false;
            });
        }
    };
    const goToNextPage = async () => {
        if (navigationInFlightRef.current) {
            return;
        }

        navigationInFlightRef.current = true;
        const nextPageIndex = currentPageIndex + 1;
        const queryAtNavigationStart = effectiveQuery;
        try {
            if (!providerListingsQuery.data) {
                await providerListingsQuery.refetch();
                return;
            }

            if (hasCachedNextPage) {
                setCurrentPageIndex(nextPageIndex);
                return;
            }

            const result = await providerListingsQuery.fetchNextPage();
            const nextPage = result.data?.pages.find(
                (page) => page.page === nextPageIndex,
            );

            if (effectiveQueryRef.current !== queryAtNavigationStart) {
                return;
            }

            if (nextPage?.listingReferences.length) {
                setCurrentPageIndex(nextPageIndex);
            }
        } finally {
            queueMicrotask(() => {
                navigationInFlightRef.current = false;
            });
        }
    };

    const retryFailedPage = listingHydrationIsError
        ? async () => {
              await listingHydration.refetch();
          }
        : providerListingsQuery.isFetchPreviousPageError
          ? goToPreviousPage
          : goToNextPage;
    const pageRangeStart = currentPageCards.length > 0 ? pageOffset + 1 : 0;
    const pageRangeEnd = Math.min(
        pageRangeStart + Math.max(0, currentPageCards.length - 1),
        resolvedTotalCount,
    );

    return {
        featuredCardsMobile: initialCards.slice(0, 4),
        featuredCardsDesktop: initialCards.slice(0, 9),
        shouldShowMobileViewAll: totalCount > 4,
        shouldShowDesktopViewAll: totalCount > 9,
        currentPageCards,
        currentPageIndex,
        resolvedTotalCount,
        pageRangeStart,
        pageRangeEnd,
        hasNextPage,
        isQueryActive: Boolean(effectiveQuery),
        isPageLoading,
        isSearching,
        panelState,
        hasPageError,
        goToPreviousPage,
        goToNextPage,
        retryFailedPage,
        isMobileSheetOpen,
        setIsMobileSheetOpen,
        isDesktopDialogOpen,
        setIsDesktopDialogOpen,
    };
}
